import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {
  FINANCING_ENDING_SKIP_REASON,
  FINANCING_ENDING_SKIP_REASON_BY_CODE,
  type FinancingEndingBulkOperationItemResponseBody,
} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {
  FINANCING_ENDING_OPERATION_ITEM_RESULT,
  type FinancingEndingOperationItemResult,
} from '../constants/FINANCING_ENDING_OPERATION_ITEM_RESULT';
import {getFinancingEndingReasonLabel} from './getFinancingEndingReasonLabel';

export type FinancingEndingOperationItemReason = {
  label: string;
  hint: string | null;
};

const isIncludedInOtherSending = (code: string | null) =>
  !isNil(code) &&
  FINANCING_ENDING_SKIP_REASON_BY_CODE[code] ===
    FINANCING_ENDING_SKIP_REASON.INCLUDED_IN_OTHER_SENDING;

/**
 * The reason of a row without its own e-mail outcome comes from its error code; a not delivered or
 * blocked e-mail has one fixed explanation. Sent and pending rows have no reason. A row that another
 * sending already includes gets no fix hint: there is no data to fix.
 */
export const getFinancingEndingOperationItemReason = (
  item: Pick<FinancingEndingBulkOperationItemResponseBody, 'errorCode'>,
  result: FinancingEndingOperationItemResult | null
) =>
  match<FinancingEndingOperationItemResult | null, FinancingEndingOperationItemReason | null>(
    result
  )
    .with(FINANCING_ENDING_OPERATION_ITEM_RESULT.SKIPPED, () => ({
      label: getFinancingEndingReasonLabel(item.errorCode),
      hint: isIncludedInOtherSending(item.errorCode)
        ? null
        : i18n.t('entity.businessCase.financingEnding.operation.skippedHint'),
    }))
    .with(FINANCING_ENDING_OPERATION_ITEM_RESULT.FAILED, () => ({
      label: getFinancingEndingReasonLabel(item.errorCode),
      hint: null,
    }))
    .with(FINANCING_ENDING_OPERATION_ITEM_RESULT.NOT_DELIVERED, () => ({
      label: i18n.t('entity.businessCase.financingEnding.operation.hint.notDelivered'),
      hint: null,
    }))
    .with(FINANCING_ENDING_OPERATION_ITEM_RESULT.BLOCKED, () => ({
      label: i18n.t('entity.businessCase.financingEnding.operation.hint.blocked'),
      hint: null,
    }))
    .otherwise(() => null);
